import React from 'react';
import { Link } from 'react-router-dom';
import { MapPinIcon, AwardIcon, BookOpenIcon, GraduationCapIcon, BuildingIcon, PhoneIcon, MailIcon, ArrowRightIcon } from 'lucide-react';
import { universities } from '../data/universities';
import { usePageMeta } from '../hooks/usePageMeta';
const brandColors: Record<string, {
  gradient: string;
  accent: string;
  tag: string;
}> = {
  'global-tech-college': {
    gradient: 'bg-gradient-to-br from-[#1B365D] to-[#8B1A1A]',
    accent: 'text-[#D4A843]',
    tag: 'bg-[#1B365D]/10 text-[#1B365D]'
  },
  'ultimate-medical-academy': {
    gradient: 'bg-gradient-to-br from-[#003B5C] to-[#176B9C]',
    accent: 'text-[#00BFA5]',
    tag: 'bg-[#003B5C]/10 text-[#003B5C]'
  },
  'university-of-arizona-global-campus': {
    gradient: 'bg-gradient-to-br from-[#1C2B39] to-[#003366]',
    accent: 'text-[#CC0033]',
    tag: 'bg-[#1C2B39]/10 text-[#1C2B39]'
  },
  'colorado-christian-university': {
    gradient: 'bg-gradient-to-br from-[#0F3057] to-[#1A4A7A]',
    accent: 'text-[#D4A943]',
    tag: 'bg-[#0F3057]/10 text-[#0F3057]'
  },
  'houston-christian-university': {
    gradient: 'bg-gradient-to-br from-[#002F6C] to-[#001A3D]',
    accent: 'text-[#A6192E]',
    tag: 'bg-[#002F6C]/10 text-[#002F6C]'
  }
};
const defaultBrand = {
  gradient: 'bg-navy',
  accent: 'text-gold',
  tag: 'bg-navy/5 text-navy'
};
export function CollegesPage() {
  usePageMeta('Browse Online Colleges & Universities | OnlineCollegeGuide', 'Explore accredited online colleges and universities. Compare programs, read reviews, and request information from top schools.', {
    ogImage: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  return <main className="flex-grow bg-light" data-id="element-1245" data-mp="2qn9wh930d7fi">
      {/* Hero */}
      <div className="hero-bg text-white py-20" data-id="element-1246" data-mp="2qlozlct81ceh">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1247" data-mp="2mnhiuhyrcb3a">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 border border-white/15 text-gold text-sm font-semibold mb-6" data-id="element-1248" data-mp="2tq68ik2gqv8h">
            <BuildingIcon className="w-4 h-4 mr-2" data-id="element-1249" data-mp="2yp0ozfcyk7pf" />
            Partner Schools
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 text-white" data-id="element-1250" data-mp="33mj11em9v31n">
            Get More Information About
            <br className="hidden sm:block" data-id="element-1251" data-mp="3cge4mi7qwlf6" /> Our Partner Colleges
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed" data-id="element-1252" data-mp="3hf8o7mdjee0c">
            Explore accredited colleges and universities offering online
            programs. Select a school to learn more and request information.
          </p>
        </div>
      </div>

      {/* Listings */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-id="element-1253" data-mp="3rb29bu5bnftq">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-1254" data-mp="1s1vh5pe1ibvj">
          {universities.map(uni => {
          const brand = brandColors[uni.slug] || defaultBrand;
          return <Link key={uni.slug} to={`/colleges/${uni.slug}`} className="group bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-xl hover:border-gray-300 transition-all duration-300 flex flex-col" data-id="element-1255" data-mp="e6dod28gzisa">
                {/* Card Header with School Brand Colors */}
                <div className={`${brand.gradient} p-6 lg:p-7 flex items-center gap-4 relative overflow-hidden`} data-id="element-1256" data-mp="x23sauzyqp8t">
                  {uni.logoUrl ? <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center flex-shrink-0 overflow-hidden p-2 shadow-lg" data-id="element-1257" data-mp="o1nqdpyvbq62">
                      <img src={uni.logoUrl} alt={`${uni.name} logo`} className="w-full h-full object-contain" data-id="element-1258" data-mp="j33yl8xlfk5s" />
                    </div> : <div className="w-14 h-14 rounded-xl bg-white/15 border-2 border-white/25 flex items-center justify-center flex-shrink-0" data-id="element-1259" data-mp="vkmvtjss6103">
                      <GraduationCapIcon className="w-7 h-7 text-white" data-id="element-1260" data-mp="10dpu258i5ymm" />
                    </div>}
                  <div className="relative z-10 min-w-0" data-id="element-1261" data-mp="rjb51ay679qb">
                    <h2 className="text-lg font-bold text-white leading-tight" data-id="element-1262" data-mp="rjta8x2t8yad">
                      {uni.name}
                    </h2>
                    <div className="flex items-center text-white/70 text-sm mt-1.5" data-id="element-1263" data-mp="1f71bn3x4bn74">
                      <MapPinIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-1264" data-mp="1j8d5xmqxswln" />
                      {uni.location}
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6 flex flex-col flex-grow" data-id="element-1265" data-mp="1abm5ky5cqvl9">
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed line-clamp-3" data-id="element-1266" data-mp="3q05jiq28rntc">
                    {uni.description}
                  </p>

                  {/* Accreditation */}
                  <div className="flex items-start text-xs text-gray-500 mb-4 bg-gray-50 rounded-lg p-3" data-id="element-1267" data-mp="3nf69ljmhtbvp">
                    <AwardIcon className="w-4 h-4 mr-2 text-gold flex-shrink-0 mt-0.5" data-id="element-1268" data-mp="3igmoflfq444e" />
                    <span className="leading-relaxed" data-id="element-1269" data-mp="aqb0vb5gujq">{uni.accreditation}</span>
                  </div>

                  {/* Contact Info. Stacked rather than side by side: at a third
                      of the row an email truncates to uselessness in a column. */}
                  <div className="space-y-2 mb-4" data-id="element-1270" data-mp="2zr6gid1pl9dy">
                    <div className="flex items-center text-xs text-gray-500" data-id="element-1271" data-mp="2zsrfqfyyxgi5">
                      <PhoneIcon className="w-3.5 h-3.5 mr-2 text-gray-400 flex-shrink-0" data-id="element-1272" data-mp="3dj39ejb7bnbe" />
                      <span data-id="element-1273" data-mp="38qju9hwv8k4h">{uni.phone}</span>
                    </div>
                    <div className="flex items-center text-xs text-gray-500 min-w-0" data-id="element-1274" data-mp="2l1qwrh0li47r">
                      <MailIcon className="w-3.5 h-3.5 mr-2 text-gray-400 flex-shrink-0" data-id="element-1275" data-mp="2g2xfddi6tt2b" />
                      <span className="truncate" data-id="element-1276" data-mp="2g133spst3daw">{uni.email}</span>
                    </div>
                  </div>

                  {/* Programs Preview */}
                  <div className="pt-4 border-t border-gray-100 mt-auto" data-id="element-1277" data-mp="oxkrjp534ncb">
                    <div className="flex items-center text-xs font-bold text-gray-800 uppercase tracking-wider mb-3" data-id="element-1278" data-mp="tpc8r92cpgps">
                      <BookOpenIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-1279" data-mp="yndtdyffdo3d" />
                      {uni.programs.length} Programs Available
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-1280" data-mp="5zrq6ra40lh1">
                      {uni.programs.slice(0, 3).map(prog => <span key={prog.slug} className={`text-xs ${brand.tag} px-2.5 py-1.5 rounded-lg font-medium`} data-id="element-1281" data-mp="ayuta66j4hmd">
                          {prog.shortTitle}
                        </span>)}
                      {uni.programs.length > 3 && <span className="text-xs text-gray-400 px-2 py-1.5" data-id="element-1282" data-mp="1qhxxfrjgk327">
                          +{uni.programs.length - 3} more
                        </span>}
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between gap-3" data-id="element-1283" data-mp="324amzwibuhln">
                    <span className="text-sm font-bold text-navy group-hover:text-action transition-colors duration-200" data-id="element-1284" data-mp="2x68u6jv3n3wz">
                      View Programs
                    </span>
                    <div className="w-9 h-9 rounded-full bg-navy/5 flex items-center justify-center flex-shrink-0 group-hover:bg-navy transition-colors duration-200" data-id="element-1285" data-mp="2n7jp5roaxdy5">
                      <ArrowRightIcon className="w-4 h-4 text-navy group-hover:text-white transition-colors duration-200" data-id="element-1286" data-mp="2e86a8jherx61" />
                    </div>
                  </div>
                </div>
              </Link>;
        })}
        </div>

        {/* Empty State */}
        {universities.length === 0 && <div className="text-center py-16" data-id="element-1287" data-mp="3hhd101q4o3j3">
            <BuildingIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-1288" data-mp="3hio4e0fnv99o" />
            <h3 className="text-xl font-bold text-navy mb-2" data-id="element-1289" data-mp="3meljk3f6u9kv">
              No colleges listed yet
            </h3>
            <p className="text-gray-500" data-id="element-1290" data-mp="37oo2s8jl8m5p">
              Check back soon — we're adding new partner schools.
            </p>
          </div>}
      </div>
    </main>;
}